import {
  ChangeEvent,
  ChangeEventHandler,
  FocusEventHandler,
  KeyboardEventHandler,
  MouseEventHandler,
  RefObject,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { InputMask } from "../InputMask";
import { TextFieldProps } from "../TextField";
import {
  CalendarProps,
  CalendarValue,
  CalendarRangeValue,
} from "../Calendar/Calendar";
import { Calendar } from "../Calendar";
import { useKeyboardFocusables, useMergeRefs } from "../../hooks";
import { IanaZone, Locale } from "../../types";
import { Icon } from "../Icon";
import Event from "@servicetitan/hammer-icon/mdi/round/event.svg";
import { DateTime } from "luxon";
import { isValidDate, toDateString } from "../Calendar/utils";
import { supportsPopover } from "../../internal/functions/supportsPopover";
import { DateFieldLegacy, DateFieldLegacyProps } from "../DateFieldLegacy";
import { Popover } from "../Popover";
import { usePopoverCloseDelayWorkaround } from "../../internal/hooks";

const RANGE_DIVIDER = " – ";

/**
 * Common props shared between single and range date field modes
 */
interface DateFieldCommonProps {
  /** Date format for input and display */
  format?: "MM/dd/yyyy" | "dd/MM/yyyy";
  /** Locale for date formatting */
  locale?: Locale;
  /** Default timezone for date operations */
  defaultTimeZone?: IanaZone;
  /** Whether to open calendar on mount */
  openCalendar?: boolean;
  /** Whether to disable the format hint */
  disableHint?: boolean;
}

/**
 * Data structure for a single date selection
 */
export interface DateSelect {
  /** The formatted date string value */
  value: string;
  /** The ISO date string or null if invalid */
  date: string | null | undefined;
  /** Whether the date is valid according to constraints */
  isValid: boolean;
}

/**
 * Data structure for a date range selection
 */
export interface DateRangeSelect {
  /** The start date selection */
  start?: DateSelect;
  /** The end date selection */
  end?: DateSelect;
  /** The combined formatted range string */
  value: string;
}

/**
 * Props for single date selection mode
 */
interface DateFieldSingleProps {
  /** Callback when date selection changes */
  onChange?: (event: ChangeEvent<HTMLInputElement>, data: DateSelect) => void;
  /** Controlled value for the date field */
  value?: string;
  /** Default value for the date field */
  defaultValue?: string;
  /** Must be false for single date mode */
  range?: false;
}

/**
 * Props for date range selection mode
 */
interface DateFieldRangeProps {
  /** Callback when date range selection changes */
  onChange?: (
    event: ChangeEvent<HTMLInputElement>,
    data: DateRangeSelect,
  ) => void;
  /** Controlled value for the date range field */
  value?: { start?: string; end?: string };
  /** Default value for the date range field */
  defaultValue?: { start?: string; end?: string };
  /** Must be true for range mode */
  range: true;
}

/**
 * Props for the DateField component
 * @extends Omit<CalendarProps, "onChange" | "value" | "defaultValue">
 * @extends Omit<TextFieldProps, "prefix" | "suffix" | "hint" | "onChange" | "showCounter" | "value" | "defaultValue">
 * @extends DateFieldCommonProps
 */
export type DateFieldProps = Omit<
  CalendarProps,
  "onChange" | "value" | "defaultValue"
> &
  Omit<
    TextFieldProps,
    | "prefix"
    | "suffix"
    | "hint"
    | "onChange"
    | "showCounter"
    | "value"
    | "defaultValue"
  > &
  DateFieldCommonProps &
  (DateFieldSingleProps | DateFieldRangeProps);

const isoToDateFormat = (date: string, format: string) => {
  // Returns a string of the proper format for ISO strings and the input date string for non-ISO strings
  const luxonDate = DateTime.fromISO(date);
  if (luxonDate.isValid) {
    return luxonDate.toFormat(format);
  }
  return date;
};

/**
 * DateField component for selecting dates with calendar popup and input validation
 * @deprecated Use DateFieldSingle or DateFieldRange instead
 *
 * Features:
 * - Single date and date range selection modes
 * - Calendar popup with keyboard navigation
 * - Input mask validation with format hints
 * - Configurable date formats (MM/dd/yyyy, dd/MM/yyyy)
 * - Min/max date constraints and unavailable dates
 * - Locale and timezone support
 * - Accessibility with ARIA attributes
 * - Keyboard navigation and focus management
 * - Popover and legacy fallback modes
 *
 * @param props - The component props
 * @param ref - Forwarded ref to the input element
 * @returns A date field component with calendar integration
 *
 * @example
 * <DateField
 *   label="Select Date"
 *   format="MM/dd/yyyy"
 *   onChange={(event, data) => console.log(data.date)}
 * />
 *
 * @example
 * <DateField
 *   label="Date Range"
 *   range={true}
 *   onChange={(event, data) => console.log(data.start?.date, data.end?.date)}
 * />
 */
export const DateField = forwardRef<HTMLInputElement, DateFieldProps>(
  (props, ref) => {
    const {
      // DateField
      openCalendar,
      onFocus,
      onBlur,
      onChange,
      onClick,
      format = "MM/dd/yyyy",
      disableHint,

      // Calendar
      minDate,
      maxDate,
      unavailableDates,
      unavailable,
      startDay,
      defaultValue,
      value: inputValue,

      // Shared
      // locale = Intl.DateTimeFormat().resolvedOptions().locale as Locale,
      defaultTimeZone = Intl.DateTimeFormat().resolvedOptions()
        .timeZone as IanaZone,
      range,

      // Input
      required,
      ...inputProps
    } = props;
    const [open, _setOpen] = useState(openCalendar || false);
    const setOpen = (val: boolean) => {
      if (open && !val) {
        inputRef.current?.focus();
      }
      _setOpen(val);
    };

    const [internalValue, setInternalValue] = useState<
      DateSelect | DateRangeSelect | undefined
    >();

    const inputRef = useRef<HTMLInputElement>(null);
    const popperRef = useRef<HTMLDivElement>(null);
    const documentRef = useRef<HTMLElement>(document.body);
    const hint = useMemo(
      () => (disableHint ? undefined : `Format: ${format.replace(/M/g, "m")}`),
      [format, disableHint],
    );

    const { focusables } = useKeyboardFocusables(documentRef, {
      observeChange: true,
    });

    const { focusables: popoverFocusables } = useKeyboardFocusables(popperRef, {
      observeChange: true,
    });

    const pageFocusables = focusables?.filter(
      (item) => !popoverFocusables?.includes(item),
    );

    const [popoverRef, setPopoverRef] = useState<RefObject<HTMLInputElement>>();
    const combinedRef = useMergeRefs([popoverRef, inputRef]);

    useEffect(() => {
      // Set internalValue from defaultValue
      if (!defaultValue) return;
      if (range) {
        const startDate = DateTime.fromFormat(
          isoToDateFormat(defaultValue.start ?? "", format),
          format,
          {
            zone: defaultTimeZone,
          },
        ).startOf("day");
        const endDate = DateTime.fromFormat(
          isoToDateFormat(defaultValue.end ?? "", format),
          format,
          {
            zone: defaultTimeZone,
          },
        ).startOf("day");
        const newValue: DateRangeSelect = {
          start: {
            value: isoToDateFormat(defaultValue.start ?? "", format),
            date: toDateString(startDate),
            isValid:
              startDate.isValid &&
              isValidDate(
                startDate,
                minDate,
                maxDate,
                new Set(unavailableDates || unavailable?.dates),
                {
                  dates: new Set(unavailable?.dates),
                  daysOfWeek: new Set(unavailable?.daysOfWeek),
                },
                defaultTimeZone,
              ),
          },
          end: {
            value: isoToDateFormat(defaultValue.end ?? "", format),
            date: toDateString(endDate),
            isValid:
              startDate.isValid &&
              isValidDate(
                endDate,
                minDate,
                maxDate,
                new Set(unavailableDates || unavailable?.dates),
                {
                  dates: new Set(unavailable?.dates),
                  daysOfWeek: new Set(unavailable?.daysOfWeek),
                },
                defaultTimeZone,
              ),
          },
          value:
            isoToDateFormat(defaultValue.start ?? "", format) +
            RANGE_DIVIDER +
            isoToDateFormat(defaultValue.end ?? "", format),
        };
        setInternalValue(newValue);
        return;
      }
      const date = DateTime.fromFormat(
        isoToDateFormat(defaultValue, format),
        format,
        {
          zone: defaultTimeZone,
        },
      ).startOf("day");
      const newValue = {
        value: isoToDateFormat(defaultValue, format),
        date: toDateString(date),
        isValid:
          date.isValid &&
          isValidDate(
            date,
            minDate,
            maxDate,
            new Set(unavailableDates || unavailable?.dates),
            {
              dates: new Set(unavailable?.dates),
              daysOfWeek: new Set(unavailable?.daysOfWeek),
            },
            defaultTimeZone,
          ),
      } as DateSelect;
      setInternalValue(newValue);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getCurrentValue = () => {
      if (inputValue !== undefined) {
        if (range) {
          const startDate = DateTime.fromFormat(
            isoToDateFormat(inputValue.start ?? "", format),
            format,
            {
              zone: defaultTimeZone,
            },
          ).startOf("day");
          const endDate = DateTime.fromFormat(
            isoToDateFormat(inputValue.end ?? "", format),
            format,
            {
              zone: defaultTimeZone,
            },
          ).startOf("day");
          const returnValue: DateRangeSelect = {
            start: {
              value: isoToDateFormat(inputValue.start ?? "", format),
              date: toDateString(startDate),
              isValid:
                startDate.isValid &&
                isValidDate(
                  startDate,
                  minDate,
                  maxDate,
                  new Set(unavailableDates || unavailable?.dates),
                  {
                    dates: new Set(unavailable?.dates),
                    daysOfWeek: new Set(unavailable?.daysOfWeek),
                  },
                  defaultTimeZone,
                ),
            },
            end: {
              value: isoToDateFormat(inputValue.end ?? "", format),
              date: toDateString(endDate),
              isValid:
                startDate.isValid &&
                isValidDate(
                  endDate,
                  minDate,
                  maxDate,
                  new Set(unavailableDates || unavailable?.dates),
                  {
                    dates: new Set(unavailable?.dates),
                    daysOfWeek: new Set(unavailable?.daysOfWeek),
                  },
                  defaultTimeZone,
                ),
            },
            value:
              isoToDateFormat(inputValue.start ?? "", format) +
              RANGE_DIVIDER +
              isoToDateFormat(inputValue.end ?? "", format),
          };
          return returnValue;
        }
        const date = DateTime.fromFormat(
          isoToDateFormat(inputValue, format),
          format,
          {
            zone: defaultTimeZone,
          },
        ).startOf("day");
        return {
          value: isoToDateFormat(inputValue, format),
          date: toDateString(date),
          isValid:
            date.isValid &&
            isValidDate(
              date,
              minDate,
              maxDate,
              new Set(unavailableDates || unavailable?.dates),
              {
                dates: new Set(unavailable?.dates),
                daysOfWeek: new Set(unavailable?.daysOfWeek),
              },
              defaultTimeZone,
            ),
        } as DateSelect;
      }
      return internalValue;
    };

    const handleInputFocus: FocusEventHandler<HTMLInputElement> = (ev) => {
      // DO NOT OPEN THE CALENDAR ON KEYBOARD OR MANUAL FOCUS EVENT
      onFocus?.(ev);
    };

    const handleInputBlur: FocusEventHandler<HTMLInputElement> = (ev) => {
      if (!popperRef.current?.contains(ev.relatedTarget)) {
        setOpen(false);
        onBlur?.(ev);
      }
    };

    const handleInputKeyDown: KeyboardEventHandler<HTMLInputElement> = (ev) => {
      let currentFocusIndex = 0;
      switch (ev.key) {
        case "Escape":
          setOpen(false);
          break;
        case "Tab":
          if (!open || !pageFocusables?.length) {
            break;
          }
          ev.preventDefault();
          currentFocusIndex = pageFocusables.indexOf(inputRef.current!) || 0;
          setOpen(false);
          if (ev.shiftKey) {
            if (currentFocusIndex === 0) {
              requestAnimationFrame(() =>
                pageFocusables[pageFocusables.length - 1].focus(),
              );
            } else {
              requestAnimationFrame(() =>
                pageFocusables[currentFocusIndex - 1].focus(),
              );
            }
            break;
          }
          if (pageFocusables.length > currentFocusIndex + 1) {
            requestAnimationFrame(() =>
              pageFocusables[currentFocusIndex + 1].focus(),
            );
          } else {
            requestAnimationFrame(() => pageFocusables[0].focus());
          }
          break;
        case "ArrowDown":
          if (!open) {
            setOpen(true);
            setTimeout(focusToCalendar, 200);
          } else {
            focusToCalendar();
          }
      }
    };

    const handleCalendarKeyDown: KeyboardEventHandler<HTMLDivElement> = (
      ev,
    ) => {
      switch (ev.key) {
        case "Escape":
          setOpen(false);
          inputRef.current?.focus();
      }
    };

    const handleInputClick: MouseEventHandler<HTMLInputElement> = (ev) => {
      setOpen(true);
      onClick?.(ev);
    };

    const handleInputChange: ChangeEventHandler<HTMLInputElement> = (ev) => {
      if (range) {
        const parts = ev.target.value.split(RANGE_DIVIDER);
        if (parts.length !== 2) return;
        const startDate = DateTime.fromFormat(parts[0], format, {
          zone: defaultTimeZone,
        }).startOf("day");
        const endDate = DateTime.fromFormat(parts[1], format, {
          zone: defaultTimeZone,
        }).startOf("day");
        const returnValue: DateRangeSelect = {
          start: {
            value: parts[0],
            date: toDateString(startDate),
            isValid:
              startDate.isValid &&
              isValidDate(
                startDate,
                minDate,
                maxDate,
                new Set(unavailableDates || unavailable?.dates),
                {
                  dates: new Set(unavailable?.dates),
                  daysOfWeek: new Set(unavailable?.daysOfWeek),
                },
                defaultTimeZone,
              ),
          },
          end: {
            value: parts[1],
            date: toDateString(endDate),
            isValid:
              startDate.isValid &&
              isValidDate(
                endDate,
                minDate,
                maxDate,
                new Set(unavailableDates || unavailable?.dates),
                {
                  dates: new Set(unavailable?.dates),
                  daysOfWeek: new Set(unavailable?.daysOfWeek),
                },
                defaultTimeZone,
              ),
          },
          value: ev.target.value,
        };
        setInternalValue(returnValue);
        onChange?.(ev, returnValue);
        return;
      }
      const date = DateTime.fromFormat(ev.target.value, format, {
        zone: defaultTimeZone,
      }).startOf("day");
      const newDate: DateSelect = {
        value: ev.target.value,
        date: toDateString(date),
        isValid:
          date.isValid &&
          isValidDate(
            date,
            minDate,
            maxDate,
            new Set(unavailableDates || unavailable?.dates),
            {
              dates: new Set(unavailable?.dates),
              daysOfWeek: new Set(unavailable?.daysOfWeek),
            },
            defaultTimeZone,
          ),
      };
      setInternalValue(newDate);
      onChange?.(ev, newDate);
    };

    const handleCalendarSelection = (
      data: { value: CalendarValue } | { value: CalendarRangeValue },
    ) => {
      if (range) {
        const startDate = (data.value as CalendarRangeValue)?.start ?? "";
        const endDate = (data.value as CalendarRangeValue)?.end ?? "";

        const startVal = DateTime.fromISO(startDate, {
          zone: defaultTimeZone,
        });
        const startValue = startVal.toFormat(format);
        const endVal = DateTime.fromISO(endDate, {
          zone: defaultTimeZone,
        });
        const endValue = endVal.toFormat(format);

        const newDateRange: DateRangeSelect = {
          start: {
            value: startValue,
            date: startDate,
            isValid: true,
          },
          end: {
            value: endValue,
            date: endDate,
            isValid: true,
          },
          value: startValue + RANGE_DIVIDER + endValue,
        };
        setInternalValue(newDateRange);
        const event = {
          target: {
            value: startValue + RANGE_DIVIDER + endValue,
          },
        } as ChangeEvent<HTMLInputElement>;
        handleInputChange(event);
        return;
      }
      const date = data.value as CalendarValue;
      const val = DateTime.fromISO(date || "", {
        zone: defaultTimeZone,
      });

      const value = val.toFormat(format);
      const newDate: DateSelect = {
        value,
        date,
        isValid: true,
      };
      setInternalValue(newDate);
      const event = {
        target: {
          value,
        },
      } as ChangeEvent<HTMLInputElement>;
      handleInputChange(event);
    };

    const focusToCalendar = () => {
      if (popperRef.current) {
        const currentFocusable =
          popperRef.current.querySelectorAll('[tabindex = "0"]')[0];
        if (currentFocusable) {
          (currentFocusable as HTMLButtonElement).focus();
        }
      }
    };

    const handleClickOutside = () => {
      setOpen(false);
    };

    const currentValue = getCurrentValue();

    const popoverOpen = openCalendar ?? open;

    const shouldShowCalendar = usePopoverCloseDelayWorkaround(popoverOpen);

    const calendar = range ? (
      <Calendar
        range
        onKeyDown={handleCalendarKeyDown}
        defaultFocusedDate={(internalValue as DateRangeSelect)?.start?.date}
        value={{
          start: (currentValue as DateRangeSelect)?.start?.date || undefined,
          end: (currentValue as DateRangeSelect)?.end?.date || undefined,
        }}
        onSelection={handleCalendarSelection}
        minDate={minDate}
        maxDate={maxDate}
        unavailableDates={unavailableDates}
        unavailable={unavailable}
        defaultTimeZone={defaultTimeZone}
        startDay={startDay}
      />
    ) : (
      <Calendar
        onKeyDown={handleCalendarKeyDown}
        defaultFocusedDate={(internalValue as DateSelect)?.date}
        value={(currentValue as DateSelect)?.date || undefined}
        onSelection={handleCalendarSelection}
        minDate={minDate}
        maxDate={maxDate}
        unavailableDates={unavailableDates}
        unavailable={unavailable}
        defaultTimeZone={defaultTimeZone}
        startDay={startDay}
      />
    );

    if (!supportsPopover())
      return <DateFieldLegacy {...(props as DateFieldLegacyProps)} ref={ref} />;
    return (
      <Popover
        open={popoverOpen}
        modal
        placement="bottom-start"
        disableFlipFallback
        disableTriggerFocus
        onOutsidePress={handleClickOutside}
      >
        <Popover.Trigger>
          {({ ref: iRef }) => {
            setPopoverRef(iRef as RefObject<HTMLInputElement>);
            return range ? (
              <InputMask
                mask={"99/99/9999" + RANGE_DIVIDER + "99/99/9999"}
                ref={combinedRef}
                onChange={handleInputChange}
                value={(currentValue as DateRangeSelect)?.value}
                inputProps={{
                  onFocus: handleInputFocus,
                  prefix: <Icon svg={Event} />,
                  onBlur: handleInputBlur,
                  onKeyDown: handleInputKeyDown,
                  onClick: handleInputClick,
                  hint,
                  required,
                  ...inputProps,
                }}
                customMaskHandler={[
                  {
                    match: {
                      matchPattern: new RegExp(/^(\d)\//),
                      startPosition: 0,
                      endPosition: 2,
                    },
                    apply: {
                      applyPattern: "0$1",
                      caretOffset: 2,
                    },
                  },
                  {
                    match: {
                      matchPattern: new RegExp(/^(\d)\//),
                      startPosition: 3,
                      endPosition: 5,
                    },
                    apply: {
                      applyPattern: "0$1",
                      caretOffset: 2,
                    },
                  },
                  {
                    match: {
                      matchPattern: new RegExp(/^(\d)\//),
                      startPosition: 13,
                      endPosition: 15,
                    },
                    apply: {
                      applyPattern: "0$1",
                      caretOffset: 2,
                    },
                  },
                  {
                    match: {
                      matchPattern: new RegExp(/^(\d)\//),
                      startPosition: 16,
                      endPosition: 18,
                    },
                    apply: {
                      applyPattern: "0$1",
                      caretOffset: 2,
                    },
                  },
                ]}
              />
            ) : (
              <InputMask
                mask={"99/99/9999"}
                ref={combinedRef}
                onChange={handleInputChange}
                value={(currentValue as DateSelect)?.value}
                inputProps={{
                  onFocus: handleInputFocus,
                  prefix: <Icon svg={Event} />,
                  onBlur: handleInputBlur,
                  onKeyDown: handleInputKeyDown,
                  onClick: handleInputClick,
                  hint,
                  autoComplete: "off",
                  required,
                  ...inputProps,
                }}
                customMaskHandler={[
                  {
                    match: {
                      matchPattern: new RegExp(/^(\d)\//),
                      startPosition: 0,
                      endPosition: 2,
                    },
                    apply: {
                      applyPattern: "0$1",
                      caretOffset: 2,
                    },
                  },
                  {
                    match: {
                      matchPattern: new RegExp(/^(\d)\//),
                      startPosition: 3,
                      endPosition: 5,
                    },
                    apply: {
                      applyPattern: "0$1",
                      caretOffset: 2,
                    },
                  },
                ]}
              />
            );
          }}
        </Popover.Trigger>
        <Popover.Content ref={popperRef} data-calendar-popover>
          {shouldShowCalendar && calendar}
        </Popover.Content>
      </Popover>
    );
  },
);

DateField.displayName = "DateField";
